import { useEffect, useState } from "react";
import api from "../../api";
import { fmtCLP } from "../../utils/formato";
import toast from "../../utils/toast";
import "../UserManager.css";

const BASE = "/api/insumos";

export default function ComprasInsumos() {
  const [proveedores, setProveedores] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [cabecera, setCabecera] = useState({
    fecha_compra: new Date().toISOString().slice(0, 10),
    proveedor_id: "",
    tipo_documento: "Sin documento",
    numero_documento: "",
    forma_pago: "Efectivo",
    observaciones: "",
  });
  const [detalle, setDetalle] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`${BASE}/proveedores`),
      api.get(`${BASE}/insumos`),
      api.get(`${BASE}/unidades`),
      api.get(`${BASE}/tipos-documento`),
      api.get(`${BASE}/formas-pago`),
    ]).then(([p, i, u, t, f]) => {
      setProveedores(Array.isArray(p.data) ? p.data : []);
      setInsumos(Array.isArray(i.data) ? i.data : []);
      setUnidades(Array.isArray(u.data) ? u.data : ["unidad", "kg", "g", "litro", "ml", "paquete", "caja"]);
      setTiposDoc(Array.isArray(t.data) ? t.data : ["Boleta", "Factura", "Sin documento"]);
      setFormasPago(Array.isArray(f.data) ? f.data : ["Efectivo", "Transferencia", "Cheque", "Crédito", "Otro"]);
    }).catch(() => {});
  }, []);

  const totalDetalle = detalle.reduce((s, d) => s + (Number(d.subtotal) || 0), 0);

  const agregarLinea = () => {
    const insumo = insumos[0];
    setDetalle([
      ...detalle,
      {
        insumo_id: insumo?.id || "",
        insumo_nombre: insumo?.nombre || "",
        cantidad: 1,
        unidad: insumo?.unidad_medida || "unidad",
        precio_unitario: 0,
        subtotal: 0,
      },
    ]);
  };

  const actualizarLinea = (idx, field, value) => {
    const nueva = [...detalle];
    nueva[idx] = { ...nueva[idx], [field]: value };
    if (field === "insumo_id") {
      const ins = insumos.find((i) => i.id === Number(value));
      if (ins) {
        nueva[idx].insumo_nombre = ins.nombre;
        nueva[idx].unidad = ins.unidad_medida || "unidad";
      }
    }
    const cant = Number(nueva[idx].cantidad) || 0;
    const precio = Number(nueva[idx].precio_unitario) || 0;
    nueva[idx].subtotal = Math.round(cant * precio * 100) / 100;
    setDetalle(nueva);
  };

  const quitarLinea = (idx) => {
    setDetalle(detalle.filter((_, i) => i !== idx));
  };

  const guardar = async () => {
    const items = detalle.filter((d) => d.insumo_id && Number(d.cantidad) > 0);
    if (items.length === 0) return toast("Agrega al menos un ítem con cantidad y precio.");
    setGuardando(true);
    setMensaje("");
    try {
      const payload = {
        ...cabecera,
        proveedor_id: cabecera.proveedor_id ? Number(cabecera.proveedor_id) : null,
        detalle: items.map((d) => ({
          insumo_id: Number(d.insumo_id),
          cantidad: Number(d.cantidad),
          unidad: d.unidad,
          precio_unitario: Number(d.precio_unitario),
        })),
      };
      await api.post(`${BASE}/compras`, payload);
      setMensaje("Compra registrada. Stock actualizado.");
      setCabecera({
        fecha_compra: new Date().toISOString().slice(0, 10),
        proveedor_id: "",
        tipo_documento: "Sin documento",
        numero_documento: "",
        forma_pago: "Efectivo",
        observaciones: "",
      });
      setDetalle([]);
    } catch (e) {
      toast(e?.response?.data?.error || "Error al guardar la compra");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="um-wrapper">
      <div className="um-card">
        <div className="um-header">
          <h2>Registrar compra de insumos</h2>
          <p className="um-subtitle">Al guardar, el stock de cada insumo se actualiza automáticamente. No genera movimientos de caja.</p>
        </div>

        <div className="um-form" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div className="um-field">
            <label>Fecha de compra</label>
            <input type="date" value={cabecera.fecha_compra} onChange={(e) => setCabecera({ ...cabecera, fecha_compra: e.target.value })} />
          </div>
          <div className="um-field">
            <label>Proveedor</label>
            <select value={cabecera.proveedor_id} onChange={(e) => setCabecera({ ...cabecera, proveedor_id: e.target.value })}>
              <option value="">— Sin proveedor —</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          <div className="um-field">
            <label>Tipo de documento</label>
            <select value={cabecera.tipo_documento} onChange={(e) => setCabecera({ ...cabecera, tipo_documento: e.target.value })}>
              {tiposDoc.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="um-field">
            <label>Número de documento</label>
            <input placeholder="Opcional" value={cabecera.numero_documento} onChange={(e) => setCabecera({ ...cabecera, numero_documento: e.target.value })} />
          </div>
          <div className="um-field">
            <label>Forma de pago</label>
            <select value={cabecera.forma_pago} onChange={(e) => setCabecera({ ...cabecera, forma_pago: e.target.value })}>
              {formasPago.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="um-field">
            <label>Observaciones</label>
            <input placeholder="Opcional" value={cabecera.observaciones} onChange={(e) => setCabecera({ ...cabecera, observaciones: e.target.value })} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <button type="button" className="um-btn um-btn-primary" onClick={agregarLinea}>+ Agregar ítem</button>
        </div>

        {detalle.length > 0 && (
          <div className="um-table-wrap" style={{ marginBottom: 16 }}>
            <table className="um-table">
              <thead>
                <tr>
                  <th>Insumo</th>
                  <th>Cantidad</th>
                  <th>Unidad</th>
                  <th>Precio unit.</th>
                  <th>Subtotal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {detalle.map((d, idx) => (
                  <tr key={idx}>
                    <td>
                      <select
                        value={d.insumo_id}
                        onChange={(e) => actualizarLinea(idx, "insumo_id", e.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {insumos.filter((i) => i.activo).map((i) => (
                          <option key={i.id} value={i.id}>{i.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        style={{ width: 80 }}
                        value={d.cantidad}
                        onChange={(e) => actualizarLinea(idx, "cantidad", e.target.value)}
                      />
                    </td>
                    <td>
                      <select value={d.unidad} onChange={(e) => actualizarLinea(idx, "unidad", e.target.value)}>
                        {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={{ width: 90 }}
                        value={d.precio_unitario}
                        onChange={(e) => actualizarLinea(idx, "precio_unitario", e.target.value)}
                      />
                    </td>
                    <td>{fmtCLP(d.subtotal)}</td>
                    <td>
                      <button type="button" className="um-btn um-btn-danger" onClick={() => quitarLinea(idx)}>Quitar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {detalle.length > 0 && (
          <div style={{ marginBottom: 16, fontWeight: 700, fontSize: "1.1rem" }}>
            Total: {fmtCLP(totalDetalle)}
          </div>
        )}

        {mensaje && <div className="um-pill um-pill-ok" style={{ marginBottom: 12 }}>{mensaje}</div>}
        <button
          type="button"
          className="um-btn um-btn-primary"
          onClick={guardar}
          disabled={guardando || detalle.length === 0}
        >
          {guardando ? "Guardando…" : "Registrar compra"}
        </button>
      </div>
    </div>
  );
}
